import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";

export type ParentType = "project";

export interface Milestone {
  id: number;
  project_id?: string;
  initiative_id?: string;
  title: string;
  milestone_type: string;
  due_date: string | null;
  completed_date: string | null;
  status: string;
  owner: string | null;
  progress_pct: number;
  sort_order: number;
  notes: string | null;
  /** Explicit RYG override set by the PM. NULL = not set; UI falls back
   *  to the date-derived tone (overdue / due soon / upcoming). Distinct
   *  from `status` (workflow state) and from the date-derived tone
   *  (auto-computed at render time). */
  health: "green" | "yellow" | "red" | null;
  /** "Blocked by" — other milestones this one depends on. Populated by
   *  the backend on read; written via the /blockers routes. Same parent
   *  type only in v1 (project↔project, initiative↔initiative). */
  blockers?: BlockerRef[];
  /** Inverse — milestones that depend on this one. Read-only; managed
   *  from the other side. */
  blocking?: BlockingRef[];
  /** When returned from get_all_milestones, the parent project/initiative
   *  name is joined in so the picker can render "Project X · Milestone Y"
   *  without a second fetch. Empty on the per-parent endpoint. */
  parent_name?: string | null;
  created_at: string;
  updated_at: string;
}

export interface BlockerRef {
  dep_id: number;
  id: number;
  title: string;
  due_date: string | null;
  completed_date: string | null;
  status: string | null;
  parent_id: string;
  parent_name: string | null;
}

export interface BlockingRef {
  dep_id: number;
  id: number;
  title: string;
  parent_id: string;
  parent_name: string | null;
}

const listBase = (_parent: ParentType) => "/milestones";
const idBase = (_parent: ParentType) => "/milestones/id";
const cacheKey = (parent: ParentType, parentId: string | null) =>
  ["milestones", parent, parentId] as const;

export function useAllMilestones(parent: ParentType = "project") {
  return useQuery<Milestone[]>({
    queryKey: ["milestones", "all", parent],
    queryFn: () => api.get(`${listBase(parent)}/all`).then((r) => r.data),
    staleTime: 30_000,
  });
}

export function useMilestones(parentId: string | null, parent: ParentType = "project") {
  return useQuery<Milestone[]>({
    queryKey: cacheKey(parent, parentId),
    queryFn: () => api.get(`${listBase(parent)}/${parentId}`).then((r) => r.data),
    enabled: !!parentId,
  });
}

export function useAddMilestone(parentId: string, parent: ParentType = "project") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<Milestone>) =>
      api.post(`${listBase(parent)}/${parentId}`, payload).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: cacheKey(parent, parentId) }),
  });
}

export function useUpdateMilestone(parentId: string, parent: ParentType = "project") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: Partial<Milestone> & { id: number }) =>
      api.put(`${idBase(parent)}/${id}`, patch).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cacheKey(parent, parentId) });
      qc.invalidateQueries({ queryKey: ["milestones", "all"] });
    },
  });
}

export function useDeleteMilestone(parentId: string, parent: ParentType = "project") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`${idBase(parent)}/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: cacheKey(parent, parentId) }),
  });
}

/** Add a "blocked by" edge: the given milestone is blocked by
 *  `blocker_milestone_id`. Same-type only — the picker filters cross-type
 *  blockers out. */
export function useAddMilestoneBlocker(parentId: string, parent: ParentType = "project") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ milestoneId, blockerMilestoneId }: { milestoneId: number; blockerMilestoneId: number }) =>
      api
        .post(`${idBase(parent)}/${milestoneId}/blockers`, {
          blocker_milestone_id: blockerMilestoneId,
        })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cacheKey(parent, parentId) });
      // The picker reads from get_all_milestones so other projects'
      // milestone lists are unaffected, but we refresh the whole
      // namespace defensively.
      qc.invalidateQueries({ queryKey: ["milestones"] });
    },
  });
}

export function useRemoveMilestoneBlocker(parentId: string, parent: ParentType = "project") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ milestoneId, blockerMilestoneId }: { milestoneId: number; blockerMilestoneId: number }) =>
      api
        .delete(`${idBase(parent)}/${milestoneId}/blockers/${blockerMilestoneId}`)
        .then(() => undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cacheKey(parent, parentId) });
      qc.invalidateQueries({ queryKey: ["milestones"] });
    },
  });
}
